/*
 * Where a lawn photo was taken (owner, 2026-10-30): the crew picks Front yard, Back yard or Side yard after each photo, or
 * skips. Saved in visit_photos.room. The old GroundsMen Steward saved front and back the same way, so moved photos keep
 * their label. Cleaning keeps room for its own rooms; only these three words ever show as a yard.
 */
export const YARD_SPOTS = ["front", "back", "side"] as const;
export type YardSpot = (typeof YARD_SPOTS)[number];

export const YARD_WORDS: Record<"en" | "es", Record<YardSpot, string>> = {
  en: { front: "Front yard", back: "Back yard", side: "Side yard" },
  es: { front: "Patio de enfrente", back: "Patio de atrás", side: "Lado de la casa" },
};

export const isYardSpot = (room: string | null | undefined): room is YardSpot =>
  (YARD_SPOTS as readonly string[]).includes(room ?? "");

/** The words for a photo's yard, or null when it has none (skipped, or a cleaning room). */
export function yardLabel(room: string | null | undefined, locale: "en" | "es" = "en"): string | null {
  return isYardSpot(room) ? YARD_WORDS[locale][room] : null;
}

/** Photos in yard order (front, back, side, then the ones with no yard), each group oldest first as it came. */
export function byYard<T extends { room?: string | null }>(photos: readonly T[]): T[] {
  const rank = (p: T) => (isYardSpot(p.room) ? YARD_SPOTS.indexOf(p.room) : YARD_SPOTS.length);
  return photos
    .map((p, i) => ({ p, i }))
    .sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i)
    .map((x) => x.p);
}
